/**
 * API Response Validator
 * Validates API response shapes and provides safe defaults
 */

/**
 * Common response shape validators
 */
const VALIDATORS = {
  // Customer response shape
  customer: (data) => {
    const defaults = {
      id: null,
      name: '',
      company: '',
      email: '',
      phone: '',
      address: { street: '', city: '', state: '', zip: '' },
      paymentTerms: 30,
      taxRate: 0,
      notes: '',
      isActive: true,
      createdAt: null
    };
    
    if (!data || typeof data !== 'object') return defaults;
    
    return {
      ...defaults,
      ...data,
      address: {
        ...defaults.address,
        ...(data.address || {})
      }
    };
  },
  
  // Invoice response shape
  invoice: (data) => {
    const defaults = {
      id: null,
      customerId: null,
      invoiceNumber: '',
      date: new Date().toISOString().split('T')[0],
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      status: 'draft',
      lineItems: [],
      subtotal: 0,
      taxRate: 0,
      tax: 0,
      total: 0,
      discountType: 'percent',
      discountValue: 0,
      notes: '',
      createdAt: null
    };
    
    if (!data || typeof data !== 'object') return defaults;
    
    return {
      ...defaults,
      ...data,
      lineItems: Array.isArray(data.lineItems) ? data.lineItems.map(item => ({
        id: item.id || null,
        description: item.description || '',
        hours: parseFloat(item.hours) || 0,
        rate: parseFloat(item.rate) || 0,
        date: item.date || new Date().toISOString().split('T')[0],
        category: item.category || 'labor',
        amount: parseFloat(item.amount) || 0
      })) : []
    };
  },
  
  // Time entry response shape
  entry: (data) => {
    const defaults = {
      id: null,
      customerId: null,
      date: new Date().toISOString().split('T')[0],
      hours: 0,
      minutes: 0,
      category: 'level1',
      description: '',
      quickService: '',
      status: 'pending',
      createdAt: null
    };
    
    if (!data || typeof data !== 'object') return defaults;
    
    return {
      ...defaults,
      ...data,
      hours: parseFloat(data.hours) || 0,
      minutes: parseInt(data.minutes) || 0
    };
  },
  
  // Dashboard stats response shape
  dashboardStats: (data) => {
    const defaults = {
      totalCustomers: 0,
      totalEntries: 0,
      totalRevenue: 0,
      pendingInvoices: 0,
      monthlyRevenue: []
    };
    
    if (!data || typeof data !== 'object') return defaults;
    
    return {
      ...defaults,
      ...data,
      totalCustomers: parseInt(data.totalCustomers) || 0,
      totalEntries: parseInt(data.totalEntries) || 0,
      totalRevenue: parseFloat(data.totalRevenue) || 0,
      pendingInvoices: parseInt(data.pendingInvoices) || 0
    };
  },
  
  // User response shape
  user: (data) => {
    const defaults = {
      id: null,
      username: '',
      email: '',
      role: 'user',
      widgets: [],
      isActive: true,
      createdAt: null
    };
    
    if (!data || typeof data !== 'object') return defaults;
    
    return {
      ...defaults,
      ...data,
      widgets: Array.isArray(data.widgets) ? data.widgets : []
    };
  }
};

/**
 * Validate an API response against expected shape
 * @param {Object} response - API response object
 * @param {string} type - Type of response to validate (customer, invoice, entry, etc.)
 * @param {Object} options - Additional options
 * @returns {Object} - Validated response with safe defaults
 */
export const validateResponse = (response, type, options = {}) => {
  const { dataKey = 'data' } = options;
  
  // Handle null/undefined response
  if (!response) {
    console.warn(`validateResponse: No response received for type ${type}`);
    return VALIDATORS[type] ? VALIDATORS[type](null) : null;
  }
  
  // Handle error responses
  if (response.error || response.status === 'error') {
    console.warn(`validateResponse: Error response for type ${type}:`, response.error || response.message);
    return VALIDATORS[type] ? VALIDATORS[type](null) : null;
  }
  
  // Extract data from response
  const data = response[dataKey] || response;
  
  // Apply validator if available
  if (VALIDATORS[type]) {
    return VALIDATORS[type](data);
  }
  
  // Return data as-is if no validator exists
  return data;
};

/**
 * Validate an array of items
 * @param {Array} response - API response array
 * @param {string} type - Type of each item
 * @param {string} arrayKey - Key to extract array from response
 * @returns {Array} - Array of validated items
 */
export const validateArrayResponse = (response, type, arrayKey = null) => {
  if (!response) return [];
  
  // Extract array from response
  let items = response;
  
  if (arrayKey && response[arrayKey]) {
    items = response[arrayKey];
  } else if (response.data) {
    // Check if data is the array or has a nested array
    if (Array.isArray(response.data)) {
      items = response.data;
    } else if (response.data.entries) {
      items = response.data.entries;
    } else if (response.data.invoices) {
      items = response.data.invoices;
    } else if (response.data.customers) {
      items = response.data.customers;
    }
  }
  
  // Ensure we have an array
  if (!Array.isArray(items)) {
    console.warn(`validateArrayResponse: Expected array for type ${type}, got`, typeof items);
    return [];
  }
  
  // Validate each item
  if (VALIDATORS[type]) {
    return items.map(item => VALIDATORS[type](item));
  }
  
  return items;
};

/**
 * Check if a response has required fields
 * @param {Object} data - Data to check
 * @param {Array} requiredFields - Array of required field names
 * @returns {Object} - { valid: boolean, missing: string[] }
 */
export const hasRequiredFields = (data, requiredFields) => {
  if (!data || typeof data !== 'object') {
    return { valid: false, missing: requiredFields };
  }
  
  const missing = requiredFields.filter(field => {
    const value = data[field];
    return value === undefined || value === null || value === '';
  });
  
  return {
    valid: missing.length === 0,
    missing
  };
};

/**
 * Safe property access with default value
 * @param {Object} obj - Object to access
 * @param {string} path - Dot-notation path (e.g., 'user.address.city')
 * @param {*} defaultValue - Default value if path doesn't exist
 * @returns {*} - Value at path or default
 */
export const safeGet = (obj, path, defaultValue = null) => {
  if (!obj || typeof obj !== 'object') return defaultValue;
  
  const keys = path.split('.');
  let current = obj;
  
  for (const key of keys) {
    if (current === null || current === undefined) {
      return defaultValue;
    }
    
    // Handle array indices
    const arrayMatch = key.match(/^(\w+)\[(\d+)\]$/);
    if (arrayMatch) {
      const [, arrayKey, index] = arrayMatch;
      current = current[arrayKey];
      if (!Array.isArray(current)) return defaultValue;
      current = current[parseInt(index)];
    } else {
      current = current[key];
    }
  }
  
  return current === undefined || current === null ? defaultValue : current;
};

export default {
  validateResponse,
  validateArrayResponse,
  hasRequiredFields,
  safeGet,
  VALIDATORS
};
